import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

const useSidebar = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false);
  const [isTablet, setIsTablet] = useState(() => typeof window !== 'undefined' ? (window.innerWidth >= MOBILE_BREAKPOINT && window.innerWidth < TABLET_BREAKPOINT) : false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const width = window.innerWidth;
    return width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT;
  });
  const prevModeRef = useRef<'mobile' | 'tablet' | 'desktop' | null>(
    typeof window !== 'undefined'
      ? (window.innerWidth < MOBILE_BREAKPOINT ? 'mobile' : window.innerWidth < TABLET_BREAKPOINT ? 'tablet' : 'desktop')
      : null
  );

  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    const mobile = width < MOBILE_BREAKPOINT;
    const tablet = width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT;
    
    setIsMobile(mobile);
    setIsTablet(tablet);
    
    // Auto-close mobile sidebar on resize to desktop
    if (!mobile) {
      setIsSidebarOpen(false);
    }
    
    // Reset desktop collapse state when switching to mobile
    if (mobile && isDesktopSidebarCollapsed) {
      setIsDesktopSidebarCollapsed(false);
    }

    // Auf Tablet-Breite reicht der Platz nicht für drei volle Spalten:
    // beim Wechsel in diese Breite auf die Symbolleiste einklappen, beim Wechsel zurück wieder öffnen.
    const mode = mobile ? 'mobile' : tablet ? 'tablet' : 'desktop';
    if (mode !== prevModeRef.current) {
      if (mode === 'tablet') setIsDesktopSidebarCollapsed(true);
      else if (mode === 'desktop' && prevModeRef.current === 'tablet') setIsDesktopSidebarCollapsed(false);
      prevModeRef.current = mode;
    }
  }, [isDesktopSidebarCollapsed]);

  useEffect(() => {
    
    // Add resize listener with debouncing
    let timeoutId: number;
    const debouncedHandleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(handleResize, 100);
    };
    
    window.addEventListener('resize', debouncedHandleResize);
    
    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      clearTimeout(timeoutId);
    };
  }, [handleResize]);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setIsSidebarOpen(prev => !prev);
    } else {
      setIsDesktopSidebarCollapsed(prev => !prev);
    }
  }, [isMobile]);

  const closeOnMobile = useCallback(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [isMobile]);

  const openSidebar = useCallback(() => {
    if (isMobile) {
      setIsSidebarOpen(true);
    } else {
      setIsDesktopSidebarCollapsed(false);
    }
  }, [isMobile]);

  const closeSidebar = useCallback(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    } else {
      setIsDesktopSidebarCollapsed(true);
    }
  }, [isMobile]);

  return useMemo(() => ({
    isMobile,
    isTablet,
    isSidebarOpen,
    isDesktopSidebarCollapsed,
    toggleSidebar,
    closeOnMobile,
    openSidebar,
    closeSidebar,
  }), [
    isMobile,
    isTablet,
    isSidebarOpen,
    isDesktopSidebarCollapsed,
    toggleSidebar,
    closeOnMobile,
    openSidebar,
    closeSidebar,
  ]);
};

export default useSidebar;
